import { useContext } from 'react';
import { useLocation } from 'react-router';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { KodplanContext } from '../../../../../../Components/KodplanManager/Context/KodplanContext';
import { TEDEKBokforderVariantEnum } from '../../../../../../globalQueryTypes';
import { ModelContext } from '../../../../../../Model/ModelContext/ModelContext';
import { TSelectedDetail } from '../../../../../../Types/MasterDetailTypes';
import {
  convertIKonteringValidationErrorToValidationErrorsType,
  roundToTwoDecimals,
} from '../../../../Leverantor/ObjektVyer/FakturaDel/Components/Kontering/Utils/KonteringUtils';
import {
  IFieldValidation,
  IValidateFn,
  IFieldValidations,
  IFormData,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import KonteringContext from '../../../../_Common/Components/KonteringManager/KonteringContext';
import {
  IKodstrang,
  IKodstrangDel,
  IKonteringValidationError,
} from '../../../../_Common/Components/KonteringManager/KonteringManager';
import { KonteringUtils } from '../../../../_Common/Components/KonteringManager/KonteringManagerUtils';
import {
  IKontodelDef,
  IKontoplan,
} from '../../../../_Common/Components/KonteringManager/Query/KonteringManagerQuery';
import { BokforderWrapperContext } from '../../../Rutiner/Bokforder/Contexts/BokforderWrapperContext';
import {
  getBokforderVariant,
  IUserBokforderKodbehorigheter,
} from '../Components/BokforderContext/BokforderContextManager';
import {
  BokforderType,
  BokforderType__Associations_Rader,
} from '../Queries/_QueryTypes/BokforderType';
import { createUsersWithKodbehBasedOnType } from '../Utils/BfoUtanordningUtils';
import {
  filterUsersOnKodstrang,
  getKodplanForBokfOrder,
  isRowEmpty,
} from '../Utils/BokforderUtils';
import { mixedInternAndExternKonton } from '../Utils/validationUtils';

export const validateDebetAndKreditOnSameRow = (
  rows: BokforderType__Associations_Rader[]
): IFieldValidations => {
  let result: IFieldValidations = [];

  for (let index = 0; index < rows?.length; index++) {
    const row = rows[index];

    if (row.DEBET && row.KREDIT) {
      const errorDebet: IFieldValidation = {
        fieldName: 'DEBET',
        message: 'Både debet och kredit är ifyllt på rad',
        type: 'Error',
        path: ['Rader', row.id],
      };
      const errorKredit: IFieldValidation = {
        fieldName: 'KREDIT',
        message: 'Både debet och kredit är ifyllt på rad',
        type: 'Error',
        path: ['Rader', row.id],
      };
      result?.push(errorDebet);
      result?.push(errorKredit);
    }
  }
  return result;
};

export const validateAttestantMandatory = (
  rows: BokforderType__Associations_Rader[],
  rowsTouched: { [id: string]: IFormData } | undefined
): IFieldValidations => {
  let result: IFieldValidations = [];

  for (let index = 0; index < rows?.length; index++) {
    const row = rows[index];

    if (
      !isRowEmpty(row, false) &&
      (row._Associations === null ||
        !(
          typeof row._Associations?.Attestant === 'string' ||
          typeof row._Associations?.Attestant === 'object'
        ) ||
        row._Associations?.Attestant === null)
    ) {
      const error: IFieldValidation = {
        fieldName: 'Name',
        message: 'Det saknas attestant på rad',
        type: 'Error',
        path: ['Rader', row.id, 'Attestant'],
        showAsHint: !rowsTouched?.[row.id]?._Associations?.Attestant,
      };
      result?.push(error);
    }
  }

  return result;
};

export const validateSaldo = (
  rows: BokforderType__Associations_Rader[]
): IFieldValidations => {
  let result: IFieldValidations = [];

  let sumDebet = 0;
  let sumKredit = 0;
  for (let index = 0; index < rows?.length; index++) {
    const row = rows[index];

    if (row.DEBET > 0) {
      sumDebet += row.DEBET;
    }

    if (row.KREDIT > 0) {
      sumKredit += row.KREDIT;
    }
  }

  if (roundToTwoDecimals(sumDebet) !== roundToTwoDecimals(sumKredit)) {
    const error: IFieldValidation = {
      fieldName: 'DebetAndKreditSumDiff',
      message: 'Summa debet skiljer sig från summa kredit',
      type: 'Error',
    };
    result.push(error);
  }
  return result ?? [];
};

export const validateZeroOrNegativeDebetAndKredit = (
  rows: BokforderType__Associations_Rader[]
): IFieldValidations => {
  let result: IFieldValidations = [];

  for (let index = 0; index < rows?.length; index++) {
    const row = rows[index];

    if (row.DEBET != null && row.DEBET <= 0) {
      const error: IFieldValidation = {
        fieldName: 'DEBET',
        message: 'Debet måste ha ett värde större än noll',
        type: 'Error',
        path: ['Rader', row.id],
      };
      result?.push(error);
    } else if (row.KREDIT != null && row.KREDIT <= 0) {
      const error: IFieldValidation = {
        fieldName: 'KREDIT',
        message: 'Kredit måste ha ett värde större än noll',
        type: 'Error',
        path: ['Rader', row.id],
      };
      result?.push(error);
    }
  }
  return result;
};

export const validateRowsExist = (
  rows: BokforderType__Associations_Rader[]
): IFieldValidations => {
  let result: IFieldValidations = [];
  let nrOfFilledRows = 0;

  for (let index = 0; index < rows.length; index++) {
    const row = rows[index];

    if (row.KREDIT || row.DEBET) {
      nrOfFilledRows += 1;
    }
  }

  if (nrOfFilledRows < 2) {
    const error: IFieldValidation = {
      fieldName: 'Atleast2rowsIsNeeded',
      message: 'Minst två fullständiga rader behöver registreras',
      type: 'Error',
    };

    result.push(error);
  }

  return result;
};

export const validateInternKonton = (
  rows: BokforderType__Associations_Rader[],
  kontodelDefFromKodplan: IKontodelDef[]
): IFieldValidations => {
  let result: IFieldValidations = [];

  const baskontoDelsDef = kontodelDefFromKodplan.find(
    kontodelsDef => kontodelsDef.ordningsNr === 1
  );

  const selectedBaskonton = rows.map(row => row._Associations?.Kontodel1);

  if (mixedInternAndExternKonton(selectedBaskonton, baskontoDelsDef)) {
    const error: IFieldValidation = {
      fieldName: 'DontMixInternalAndExternalAccounts',
      message: 'Du kan inte blanda extern och internkonton',
      type: 'Error',
    };
    result.push(error);
  }

  return result;
};

export const createKodstrangDelar = (
  key: string,
  kontodelDefs: IKontodelDef[],
  kontodel: any
): IKodstrangDel[] => {
  const kontodelarInKodstrang: IKodstrangDel[] = [];
  let selectedKontoId = '';
  if (typeof kontodel === 'string') {
    selectedKontoId = kontodel;
  } else {
    selectedKontoId = kontodel.id;
  }

  const possibleMatch = key.match(/\d+/g);

  const ordningsNummer = possibleMatch ? parseInt(possibleMatch[0]) : -1;

  const correctKontodelsDef = kontodelDefs.find(
    kontodelsDef => kontodelsDef.ordningsNr === ordningsNummer
  );
  const kontodelDetailed = correctKontodelsDef?.kontodelar.find(
    baskontoFromKodplan => baskontoFromKodplan.id === selectedKontoId
  );

  if (kontodelDetailed) {
    const kodstrangDel: IKodstrangDel = {
      id: kontodelDetailed.id,
      kontodel: kontodelDetailed.kontodel,
      ordningsNr: kontodelDetailed.ordningsNr,
      benamning: kontodelDetailed.benamning,
    };

    kontodelarInKodstrang.push(kodstrangDel);
  }
  return kontodelarInKodstrang;
};

export const createKodstrang = (
  row: BokforderType__Associations_Rader,
  kontodelDefs: IKontodelDef[]
): IKodstrang | null => {
  let result: IKodstrang | null = null;
  if (row._Associations && !isRowEmpty(row, false)) {
    let kontodelarInKodstrang: IKodstrangDel[] = [];
    for (const [key, value] of Object.entries(row._Associations)) {
      if (key.includes('Kontodel') && value) {
        kontodelarInKodstrang = kontodelarInKodstrang.concat(
          createKodstrangDelar(key, kontodelDefs, value)
        );
      }
    }
    if (kontodelarInKodstrang.length > 0) {
      const belopp = row.DEBET
        ? row.DEBET
          ? row.DEBET
          : 0
        : row.KREDIT
        ? row.KREDIT
        : 0;
      result = {
        id: row.id,
        belopp: belopp,
        kontodelar: kontodelarInKodstrang,
      };
    }
  }

  return result;
};

const convertOldKontodelErrorToNew = (
  oldError: IFieldValidation,
  row: IKodstrang,
  lopnr: number
): IFieldValidation => {
  const error: IFieldValidation = {
    fieldName: 'KontodelBenamning',
    message: oldError.message as any,
    type: oldError.type,
    showAsHint: oldError.showAsHint,
    path: ['Rader', row.id, `Kontodel${lopnr}`],
  };

  return error;
};

const convertKombErrorToValidation = (
  kombinationErrors: IKonteringValidationError[],
  kodstrang: IKodstrang,
  touched: IFormData | undefined
): IFieldValidation[] => {
  const result = kombinationErrors.map(kombinationError => {
    const error = convertIKonteringValidationErrorToValidationErrorsType(
      kombinationError,
      kodstrang,
      touched
    );

    return convertOldKontodelErrorToNew(
      error,
      kodstrang,
      kombinationError.lopnr
    );
  });

  return result;
};

export const validateKombinationErrors = (
  kodstrangar: IKodstrang[],
  enhancedBokforderKodplan: IKontoplan,
  bokforderDate: Date,
  konteringUtils: KonteringUtils,
  rowsTouched: { [id: string]: IFormData } | undefined
): IFieldValidations => {
  let result: IFieldValidations = [];

  for (let index = 0; index < kodstrangar.length; index++) {
    const kodstrang = kodstrangar[index];
    const touched = rowsTouched?.[kodstrang.id];
    const kombinationErrors = konteringUtils.getKombinationErrors(
      kodstrang,
      enhancedBokforderKodplan,
      bokforderDate
    );

    if (kombinationErrors.length > 0) {
      const validation = convertKombErrorToValidation(
        kombinationErrors,
        kodstrang,
        touched
      );

      result.push(...validation);
    }
  }

  return result;
};

export const validateKombinationVillkorErrors = (
  kodstrangar: IKodstrang[],
  enhancedBokforderKodplan: IKontoplan,
  bokforderDate: Date,
  konteringUtils: KonteringUtils,
  rowsTouched: { [id: string]: IFormData } | undefined
): IFieldValidation[] => {
  let result: IFieldValidation[] = [];

  for (let index = 0; index < kodstrangar.length; index++) {
    const kodstrang = kodstrangar[index];
    const touched = rowsTouched?.[kodstrang.id];
    const kombinationErrors = konteringUtils.getKombinationsVillkorsErrors(
      kodstrang,
      enhancedBokforderKodplan,
      bokforderDate
    );

    if (kombinationErrors.length > 0) {
      const validation = convertKombErrorToValidation(
        kombinationErrors,
        kodstrang,
        touched
      );

      result.push(...validation);
    }
  }

  return result;
};

export const validateKontodelErrors = (
  kodstrangar: IKodstrang[],
  enhancedBokforderKodplan: IKontoplan,
  bokforderDate: Date,
  konteringUtils: KonteringUtils,
  rowsTouched: { [id: string]: IFormData } | undefined
): IFieldValidation[] => {
  let result: IFieldValidation[] = [];
  result = kodstrangar.flatMap(kodstrang => {
    const touched = rowsTouched?.[kodstrang.id];
    const kontodelErrors = konteringUtils.getKontodelErrors(
      kodstrang,
      enhancedBokforderKodplan,
      bokforderDate,
      true
    );

    const validation = convertKombErrorToValidation(
      kontodelErrors,
      kodstrang,
      touched
    );
    return validation;
  });

  return result;
};

export const validateBaskontoMandatory = (
  rows: BokforderType__Associations_Rader[],
  rowsTouched: { [id: string]: IFormData } | undefined
): IFieldValidation[] => {
  let result: IFieldValidation[] = [];

  for (let index = 0; index < rows.length; index++) {
    const row = rows[index];

    if (!isRowEmpty(row, false)) {
      if (
        row._Associations?.Kontodel1 === undefined ||
        row._Associations?.Kontodel1 === null
      ) {
        const error: IFieldValidation = {
          fieldName: 'KontodelBenamning',
          message: 'Baskonto är obligatoriskt',
          type: 'Error',
          path: ['Rader', row.id, 'Kontodel1'],
          showAsHint: !rowsTouched?.[row.id]?._Associations?.Kontodel1,
        };

        result.push(error);
      }
    }
  }

  return result;
};

export const validateAttestantKodbeh = (
  rows: BokforderType__Associations_Rader[],
  rowsTouched: { [id: string]: IFormData } | undefined,
  usersWithKodbeh: IUserBokforderKodbehorigheter[],
  kodstrangar: IKodstrang[]
): IFieldValidation[] => {
  const errors: IFieldValidations = rows.reduce((prevErrors, currRow) => {
    // Vi kollar inte på attesterade rader
    if (currRow.ATTESTERAD_DATUM) {
      return prevErrors;
    }

    if (currRow._Associations?.Attestant) {
      const currentAttestant: IUserBokforderKodbehorigheter | undefined =
        usersWithKodbeh.find(
          user => user.userID === currRow._Associations?.Attestant?.id
        );
      const currentKodstrang = kodstrangar.find(
        kodstrang => kodstrang.id === currRow.id
      );

      if (currentAttestant && currentKodstrang) {
        const availableUsers = usersWithKodbeh.filter(
          filterUsersOnKodstrang(currentKodstrang.kontodelar)
        );

        const attestant = availableUsers.find(
          user => currentAttestant.userID === user.userID
        );

        if (!attestant) {
          const error: IFieldValidation = {
            fieldName: 'Attestant',
            type: 'Error',
            message: `Utanför ${currentAttestant?.namn}s kodbehörighet`,
            path: ['Rader', currRow.id, 'Attestant'],
            showAsHint: !rowsTouched?.[currRow.id]?._Associations?.Attestant,
          };

          return [...prevErrors, error];
        }
      }
    }

    return prevErrors;
  }, [] as IFieldValidations);

  return errors;
};

export const useValidateBokforder: (
  selectedDetail: TSelectedDetail
) => IValidateFn<BokforderType> = () => {
  const location = useLocation();
  const { userID } = useContext(ActiveDatabaseContext);
  const { avenyCollectionMap } = useContext(ModelContext);
  const bokforderWrapperContext = useContext(BokforderWrapperContext);
  const konteringContext = useContext(KonteringContext);
  const kodplaner = useContext(KodplanContext);

  return ({ currentValues, touched }) => {
    let errors: IFieldValidations = [];
    if (!konteringContext.konteringValidationQueryLoading) {
      const dataMergedWithChanges = currentValues;

      const rows = dataMergedWithChanges?._Associations?.Rader;
      const rowsTouched: { [id: string]: IFormData } | undefined =
        touched?._Associations?.Rader;

      if (rows == null) {
        return errors;
      }

      const bokforderDate = currentValues?.BOKF_DATUM
        ? new Date(currentValues?.BOKF_DATUM)
        : new Date();

      const bokforderType = getBokforderVariant(currentValues, location.search);

      const bokforderKodplan = getKodplanForBokfOrder(
        kodplaner.availableKodplanList,
        bokforderDate
      );

      const enhancedBokforderKodplan =
        konteringContext.konteringUtils.getKontoPlanById(
          bokforderKodplan?.id ?? ''
        );

      const allBokforderUsersWithKodbeh: IUserBokforderKodbehorigheter[] =
        createUsersWithKodbehBasedOnType(
          bokforderWrapperContext.allBFOUsers,
          avenyCollectionMap,
          bokforderDate,
          'FOR_BOKFORDER',
          userID ?? '',
          bokforderWrapperContext.isEconomyPersonnel
        );

      const kodstrangar: IKodstrang[] = rows.reduce<IKodstrang[]>(
        (prev, row) => {
          const potentialKodstrang = createKodstrang(
            row,
            enhancedBokforderKodplan?.kontodelDefs ?? []
          );

          if (potentialKodstrang !== null) {
            return [...prev, potentialKodstrang];
          } else {
            return prev;
          }
        },
        []
      );

      // Får ej ha debet och kredit på samma rad
      errors = errors.concat(validateDebetAndKreditOnSameRow(rows));

      // Får ej ha negativa eller 0 kr på debet och kredit
      errors = errors.concat(validateZeroOrNegativeDebetAndKredit(rows));

      // Validera differensen på all Debet och Kredit ska bli 0
      errors = errors.concat(validateSaldo(rows));

      // Validera så att det minst finns 2 rader i bokfordern
      errors = errors.concat(validateRowsExist(rows));

      // Validera så att användaren inte blandar inter och extern konton
      errors = errors.concat(
        validateInternKonton(rows, enhancedBokforderKodplan?.kontodelDefs ?? [])
      );

      // Obligatoriskt med attestant på rad
      errors = errors.concat(validateAttestantMandatory(rows, rowsTouched));

      // Obligatoriskt med en värde på baskonto
      errors = errors.concat(validateBaskontoMandatory(rows, rowsTouched));

      if (enhancedBokforderKodplan && kodstrangar.length > 0) {
        // Validera kontodel i kodsträngen
        errors = errors.concat(
          validateKontodelErrors(
            kodstrangar,
            enhancedBokforderKodplan,
            bokforderDate,
            konteringContext.konteringUtils,
            rowsTouched
          )
        );
        // Validera kombination i kodsträngen
        errors = errors.concat(
          validateKombinationErrors(
            kodstrangar,
            enhancedBokforderKodplan,
            bokforderDate,
            konteringContext.konteringUtils,
            rowsTouched
          )
        );
        // Validera kombinationvilkor i kodsträngen
        errors = errors.concat(
          validateKombinationVillkorErrors(
            kodstrangar,
            enhancedBokforderKodplan,
            bokforderDate,
            konteringContext.konteringUtils,
            rowsTouched
          )
        );

        errors = errors.concat(
          validateAttestantKodbeh(
            rows,
            rowsTouched,
            allBokforderUsersWithKodbeh,
            kodstrangar
          )
        );
      }

      if (bokforderType === TEDEKBokforderVariantEnum.PERIODISERA) {
        const havePeriodOnAllRows = rows.every(
          row =>
            (row.REF_LOPNR !== -1 && row.REF_LOPNR !== null) ||
            (row.PERIODISERAD_FROM_DATUM !== '' &&
              row.PERIODISERAD_FROM_DATUM !== null) ||
            (row.PERIODISERAD_TOM_DATUM !== '' &&
              row.PERIODISERAD_TOM_DATUM !== null)
        );

        if (!havePeriodOnAllRows) {
          const error: IFieldValidation = {
            fieldName: 'AllRowsMustHavePeriod',
            message:
              'Alla rader måste periodiseras vid periodisering av befintlig verifikation',
            type: 'Error',
          };
          errors.push(error);
        }
      }
    }

    return errors;
  };
};

export default useValidateBokforder;
